import { VoyageSchedulesModelBase } from './voyage-schedules-model';
import { SchedulesService } from '../services/schedules-service';
import { deepEqual } from '../utils/helpers';
import { FetchStatusCodeError } from '../utils/auth-fetch';
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { ScheduleEntry, IMOsCurrentScheduleState, ScheduleEvents, SimplifiedPortCall, CurrentScheduleState, GetSchedulePromise, ScheduleCacheEntry } from './types/voyage-schedules-model.types';

const cacheTimeoutMinutes = 15;
const isVessel = () => (<any>window)["appenv"].vesselIMONumber !== undefined;

export class VoyageSchedulesModelImpl extends VoyageSchedulesModelBase {
    constructor() {
        super();

        this.setLifecycleTimeout(cacheTimeoutMinutes);
        this._loadSchedules();
        this._refreshVesselStates();
        setInterval(() => this._refreshVesselStates(), 15 * 60 * 1000); //refresh every 15 minutes
    }

    get currentScheduleStates(): IMOsCurrentScheduleState[] {
        const res = [];
        for (let [imoNo, schedule] of this._currentScheduleStates) {
            res.push({
                imoNo,
                schedule
            })
        }
        return res;
    }

    async getScheduleFor(imoNo: number, includeNumBefore = 0, realtiveFrom = 0): Promise<ScheduleEntry[] | null> {
        if (this._schedulePromises.has(imoNo)) {
            // Already fetching, wait for it
            await this._schedulePromises.get(imoNo)!.promise;
        }

        const CACHE_KEY = `${imoNo}-${realtiveFrom}-${includeNumBefore}`;

        if (this._schedules.has(CACHE_KEY)) {
            if (Date.now() - this._schedules.get(CACHE_KEY)!.timestamp < 60 * 1000) {
                // Use cached version if newer than a minute
                return this._schedules.get(CACHE_KEY)!.schedule;
            }
        }

        const fromDate = new Date();
        if (realtiveFrom) {
            fromDate.setUTCMilliseconds(fromDate.getUTCMilliseconds() - realtiveFrom);
        }

        const promise = this._getScheduleFor(imoNo, includeNumBefore, fromDate);
        this._schedulePromises.set(imoNo, { promise });
        const schedule = await promise;
        if (!schedule) {
            this._schedules.delete(CACHE_KEY);
            return null;
        }

        // Emit event if there are changes
        let emitEvent = true;
        if (this._schedules.has(CACHE_KEY) && deepEqual(this._schedules.get(CACHE_KEY)!.schedule, schedule)) {
            emitEvent = false;
        }

        this._schedules.set(CACHE_KEY, { timestamp: Date.now(), schedule });

        if (emitEvent) {
            this.dispatchEvent(new CustomEvent(ScheduleEvents.ScheduleUpdated, { detail: { imo: imoNo } }));
        }
        return schedule;
    }

    getDatesFromSchedule(imoNo: number) {
        const msPerHour = 60 * 60 * 1000;
        const currentTime = new Date();
        let toDate = currentTime;
        let fromDate;
        const state = this._currentScheduleStates.get(imoNo);

        if (state && state.startTime && state.endTime) {
            fromDate = new Date(state.startTime);
            toDate = new Date(Math.min(currentTime.getTime(), state.endTime - msPerHour));
        } else {
            // TBD Fallback: Get tracking 10 days back.
            const period = 10 * 24 * msPerHour;
            fromDate = new Date(currentTime.getTime() - period);
        }

        return { toDate, fromDate, currentTime };
    }

    async changePortCall(portCall: SimplifiedPortCall): Promise<void> {
        if (!isVessel()) {
            throw new Error("changePortCall only available on vessel!");
        }

        try {
            await SchedulesService.changePortCall({
                gsisId: portCall.gsisId,
                id: portCall.localId ? portCall.localId : undefined,
                terminalCode: portCall.terminalCode,
                arrivalAt: portCall.arrivalAt,
                departureAt: portCall.departureAt
            });
        }
        catch (err) {
            // Show an error dialog and re-throw error
            this._showError(<Error>err);
            throw err;
        }

        await this._refreshVesselStates();

        this._schedules.clear();
        await this.getScheduleFor((<any>window)["appenv"].vesselIMONumber);
    }

    async addPortCall(portCall: SimplifiedPortCall): Promise<void> {
        if (!isVessel()) {
            throw new Error("addPortCall only available on vessel!");
        }

        try {
            await SchedulesService.addPortCall({
                gsisId: null,
                terminalCode: portCall.terminalCode,
                arrivalAt: portCall.arrivalAt,
                departureAt: portCall.departureAt
            });
        }
        catch (err) {
            // Show an error dialog and re-throw error
            this._showError(<Error>err);
            throw err;
        }

        await this._refreshVesselStates();

        this._schedules.clear();
        await this.getScheduleFor((<any>window)["appenv"].vesselIMONumber);
    }

    async deletePortCall(portCall: SimplifiedPortCall): Promise<void> {
        if (!isVessel()) {
            throw new Error("deletePortCall only available on vessel!");
        }

        try {
            await SchedulesService.deletePortCall(portCall.gsisId, portCall.localId);
        }
        catch (err) {
            // Show an error dialog and re-throw error
            this._showError(<Error>err);
            throw err;
        }

        await this._refreshVesselStates();

        this._schedules.clear();
        await this.getScheduleFor((<any>window)["appenv"].vesselIMONumber);
    }

    private _schedules: Map<string, ScheduleCacheEntry> = new Map();
    private _schedulePromises: Map<number, GetSchedulePromise> = new Map();
    private _currentScheduleStates: Map<number, CurrentScheduleState> = new Map();

    private _storeSchedules() {
        const arr = Array.from(this._currentScheduleStates.entries());
        localStorage.setItem('voyage-schedules-model', JSON.stringify(arr));
    }

    private _loadSchedules() {
        try {
            this._currentScheduleStates = new Map(JSON.parse(localStorage.getItem('voyage-schedules-model') || '[]'));

            this.setCacheLifecycle();
            this.dispatchEvent(new Event(ScheduleEvents.ScheduleStatesUpdated));
        } catch (error) {
            this._storeSchedules();
        }
    }

    private async _refreshVesselStates() {
        const schedulesStates = await SchedulesService.fetchScheduleVesselStates();
        if (!schedulesStates) {
            return;
        }

        this._currentScheduleStates.clear();

        schedulesStates.forEach((item: { imoNo: number; schedule: CurrentScheduleState; }) => {
            this._currentScheduleStates.set(item.imoNo, item.schedule);
        });

        this._lastFetchedTs = (new Date()).getTime();
        this._storeSchedules();
        this.setCompleteLifecycle();
        this.dispatchEvent(new Event(ScheduleEvents.ScheduleStatesUpdated));
    }

    private async _getScheduleFor(imoNo: number, includeNumBefore = 0, fromDate?: Date): Promise<ScheduleEntry[]> {
        return (await SchedulesService.fetchScheduleDetailsForVessel(imoNo, isVessel() ? 1 : includeNumBefore, fromDate))[imoNo];
    }

    private async _showError(err: Error) {
        let errorDetails = err.message;
        let bodyText = "An error occurred while attempting to update the schedule";

        if (err instanceof FetchStatusCodeError) {
            try {
                const json = await err.response.json();
                if (json.err && json.err.error) {
                    errorDetails = json.err.error;
                }
            }
            catch (e) { }
        }

        if (errorDetails === "arrivalAt time is before departureAt time") {
            bodyText = "Cannot save a port call with arrival time before departure time. Please correct port calls arrival and/or departure time.";
        }

        VoyageDialogPopupManager.createDialog({
            headerText: "Error when updating schedule",
            bodyText: bodyText,
            isError: true,
            errorDetails: errorDetails
        });
    }

}
